import { AxiosError } from 'axios';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import ResponsiveChart, { METRIC_COLOR_MAP } from '../components/ResponsiveChart';
import { PageHeader } from '../components/ui';
import { ExpandChevron, ExpandableRow } from '../components/ui/ExpandableRow';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toDateInputValue, todayDateInputValue } from '../utils/dateInput';
// import { ErrorHandler } from '../utils/errorHandler';

interface FermentationMaintenanceRecord {
  id: number;
  fermentation_id?: number;
  wine_batch_id?: number; // Kept for display (from fermentation.wineBatch), deprecated for form
  temperature?: number;
  rs?: number;
  sg?: number;
  total_so2?: number;
  et?: number;
  ph?: number;
  ta?: number;
  alc?: number;
  density_sg?: number;
  brix?: number;
  fk15_g_hl?: number;
  wine_volume?: number;
  bisulfite?: number;
  tartaric_acid?: number;
  yeast_strain?: string;
  yeast_type?: string;
  yeast_volume?: string;
  reading_timestamp: string;
  taken_by_id: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  wineBatch?: {
    id: number;
    batch_id: string;
    batch_name: string;
    wine_type?: string;
    start_date?: string;
    vineyard?: {
      name: string;
    };
  };
  takenBy?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface FermentationMaintenanceFormValues {
  fermentation_id?: number | null;
  temperature?: number | null;
  rs?: number | null;
  sg?: number | null;
  total_so2?: number | null;
  et?: number | null;
  ph?: number | null;
  ta?: number | null;
  alc?: number | null;
  density_sg?: number | null;
  brix?: number | null;
  fk15_g_hl?: number | null;
  wine_volume?: number | null;
  bisulfite?: number | null;
  tartaric_acid?: number | null;
  yeast_strain?: string | null;
  yeast_type?: string | null;
  yeast_volume?: string | null;
  reading_timestamp?: string;
  notes?: string;
}

// Mapping function to normalize data from the API
const mapApiDataToMaintenanceRecord = (apiData: any): FermentationMaintenanceRecord => {
  // fermentation_id is the actual FK (maintenance is linked to fermentation, not wine batch)
  const fermentationId = apiData.fermentation_id ?? apiData.fermentationId ?? null;

  const mapped: FermentationMaintenanceRecord = {
    id: apiData.id,
    fermentation_id: fermentationId,
    temperature: apiData.temperature,
    rs: apiData.rs,
    sg: apiData.sg,
    total_so2: apiData.total_so2 || apiData.totalSo2,
    et: apiData.et,
    ph: apiData.ph,
    ta: apiData.ta,
    alc: apiData.alc,
    density_sg: apiData.density_sg || apiData.densitySg,
    brix: apiData.brix,
    fk15_g_hl: apiData.fk15_g_hl || apiData.fk15GHl,
    wine_volume: apiData.wine_volume || apiData.wineVolume,
    bisulfite: apiData.bisulfite,
    tartaric_acid: apiData.tartaric_acid || apiData.tartaricAcid,
    yeast_strain: apiData.yeast_strain || apiData.yeastStrain,
    yeast_type: apiData.yeast_type || apiData.yeastType,
    yeast_volume: apiData.yeast_volume || apiData.yeastVolume,
    reading_timestamp: apiData.reading_timestamp || apiData.readingTimestamp || new Date().toISOString(),
    taken_by_id: apiData.taken_by_id || apiData.takenById,
    notes: apiData.notes,
    created_at: apiData.created_at || apiData.createdAt || new Date().toISOString(),
    updated_at: apiData.updated_at || apiData.updatedAt || new Date().toISOString()
  };

  // Map wineBatch for display (from fermentation.wineBatch or direct wineBatch in API response)
  if (apiData.wineBatch) {
    mapped.wineBatch = {
      id: apiData.wineBatch.id,
      batch_id: apiData.wineBatch.batch_id || apiData.wineBatch.batchId || `#${apiData.wineBatch.id}`,
      batch_name: apiData.wineBatch.batch_name || apiData.wineBatch.batchName || 'Unnamed Batch',
      wine_type: apiData.wineBatch.wine_type || apiData.wineBatch.wineType,
      start_date: apiData.wineBatch.start_date || apiData.wineBatch.startDate
    };

    if (apiData.wineBatch.vineyard) {
      mapped.wineBatch.vineyard = { name: apiData.wineBatch.vineyard.name };
    }
  } else if (apiData.fermentation && apiData.fermentation.wineBatch) {
    mapped.wineBatch = {
      id: apiData.fermentation.wineBatch.id,
      batch_id: apiData.fermentation.wineBatch.batch_id || apiData.fermentation.wineBatch.batchId || `#${apiData.fermentation.wineBatch.id}`,
      batch_name: apiData.fermentation.wineBatch.batch_name || apiData.fermentation.wineBatch.batchName || 'Unnamed Batch',
      wine_type: apiData.fermentation.wineBatch.wine_type || apiData.fermentation.wineBatch.wineType,
      start_date: apiData.fermentation.wineBatch.start_date || apiData.fermentation.wineBatch.startDate
    };

    if (apiData.fermentation.wineBatch.vineyard) {
      mapped.wineBatch.vineyard = { name: apiData.fermentation.wineBatch.vineyard.name };
    }
  }

  // Map takenBy user
  if (apiData.takenBy) {
    mapped.takenBy = {
      id: apiData.takenBy.id,
      first_name: apiData.takenBy.first_name || apiData.takenBy.firstName,
      last_name: apiData.takenBy.last_name || apiData.takenBy.lastName,
      email: apiData.takenBy.email
    };
  }

  return mapped;
};

const validationSchema = Yup.object({
  fermentation_id: Yup.number().required('Fermentation is required').typeError('Please select a fermentation'),
  temperature: Yup.number().nullable(),
  rs: Yup.number().nullable(),
  sg: Yup.number().nullable(),
  total_so2: Yup.number().nullable(),
  et: Yup.number().nullable(),
  ph: Yup.number().nullable(),
  ta: Yup.number().nullable(),
  alc: Yup.number().nullable(),
  density_sg: Yup.number().nullable(),
  brix: Yup.number().nullable(),
  fk15_g_hl: Yup.number().nullable(),
  wine_volume: Yup.number().nullable(),
  bisulfite: Yup.number().nullable(),
  tartaric_acid: Yup.number().nullable(),
  yeast_strain: Yup.string().nullable(),
  yeast_type: Yup.string().nullable(),
  yeast_volume: Yup.string().nullable(),
  reading_timestamp: Yup.date().required('Reading date is required'),
  notes: Yup.string().nullable()
});

const FermentationMaintenance: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [searchParams] = useSearchParams();
  const batchIdParam = searchParams.get('batchId');

  const [maintenanceRecords, setMaintenanceRecords] = useState<FermentationMaintenanceRecord[]>([]);
  const [wineBatches, setWineBatches] = useState<any[]>([]);
  const [fermentations, setFermentations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<AxiosError | string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<FermentationMaintenanceRecord | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(batchIdParam ? parseInt(batchIdParam) : null);
  const [expandedRecords, setExpandedRecords] = useState<number[]>([]);
  const [visibleMetrics, setVisibleMetrics] = useState<string[]>(['brix', 'ph', 'temperature']);
  const [isChartOpen, setIsChartOpen] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch fermentations and wine batches in parallel
        const [fermentationsRes, batchesRes] = await Promise.all([
          api.get('/api/fermentation'),
          api.get('/api/batch?include=vineyard')
        ]);

        console.log('Fetched fermentations:', fermentationsRes.data);
        console.log('Fetched wine batches:', batchesRes.data);

        // Extract batch IDs that have fermentation records
        const allowedBatchIds = new Set(
          fermentationsRes.data
            .map((ferm: any) => ferm.batch_id || ferm.batchId || ferm.wineBatch?.id)
            .filter((id: any) => Number.isFinite(id))
        );

        console.log('Batch IDs with fermentation records:', Array.from(allowedBatchIds));

        // Filter batches to only those with fermentation records
        const batchesWithFermentation = (Array.isArray(batchesRes.data) ? batchesRes.data : [])
          .filter((batch: any) => allowedBatchIds.has(Number(batch.id)));

        console.log('Wine batches with fermentation (filtered):', batchesWithFermentation);

        setWineBatches(batchesWithFermentation);
        setFermentations(Array.isArray(fermentationsRes.data) ? fermentationsRes.data : []);

        // Fetch maintenance records (either all or filtered by batch id)
        let recordsRes;
        if (selectedBatchId) {
          recordsRes = await api.get(`/api/fermentation-maintenance/batch/${selectedBatchId}?include=wineBatch,takenBy`);
        } else {
          recordsRes = await api.get('/api/fermentation-maintenance?include=wineBatch,takenBy');
        }

        if (recordsRes.data) {
          console.log('Fetched maintenance records with data:', recordsRes.data);

          // Process the data through our mapping function
          const recordsData = Array.isArray(recordsRes.data)
            ? recordsRes.data
            : [recordsRes.data];

          const mappedRecords = recordsData.map(mapApiDataToMaintenanceRecord);
          console.log('Mapped maintenance records:', mappedRecords);

          setMaintenanceRecords(mappedRecords);
          setError(null);
        } else {
          console.error('No data returned from API');
          setError('No data returned from server');
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [selectedBatchId, tabVisible]);

  const openModal = (record: FermentationMaintenanceRecord | null = null) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this maintenance record?')) return;
    try {
      await api.delete(`/api/fermentation-maintenance/${id}`);
      setMaintenanceRecords(prev => prev.filter(r => r.id !== id));
      setSuccessMessage('Maintenance record deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err);
    }
  };

  const handleSubmit = async (values: FermentationMaintenanceFormValues, { resetForm, setSubmitting }: any) => {
    try {
      let updatedRecord: FermentationMaintenanceRecord;

      const payload: FermentationMaintenanceFormValues = { ...values };

      if (editingRecord) {
        // Update existing record
        await api.put(`/api/fermentation-maintenance/${editingRecord.id}`, payload);
        // Get the updated data with all associations
        updatedRecord = await getUpdatedRecord(editingRecord.id);
        setMaintenanceRecords(maintenanceRecords.map(r => (r.id === editingRecord.id ? updatedRecord : r)));
        setSuccessMessage('Maintenance record updated successfully!');
      } else {
        // Create new record - maintenance is linked to fermentation, not wine batch
        const response = await api.post('/api/fermentation-maintenance', payload);
        // Get the new data with all associations
        updatedRecord = await getUpdatedRecord(response.data.id);
        setMaintenanceRecords([updatedRecord, ...maintenanceRecords]);
        setSuccessMessage('Maintenance record created successfully!');
      }

      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving maintenance record:', err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get maintenance record data with associations
  const getUpdatedRecord = async (recordId: number) => {
    try {
      const response = await api.get(`/api/fermentation-maintenance/${recordId}?include=wineBatch,takenBy`);
      if (response.data) {
        console.log('Raw updated record:', response.data);
        // Apply the mapping function to normalize the data
        const mappedRecord = mapApiDataToMaintenanceRecord(response.data);
        console.log('Mapped updated record:', mappedRecord);
        return mappedRecord;
      } else {
        throw new Error('No data returned from server');
      }
    } catch (error: any) {
      console.error('Error fetching updated maintenance record:', error);
      throw new Error(`Failed to retrieve updated record: ${error.message || 'Unknown error'}`);
    }
  };

  const handleBatchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "") {
      setSelectedBatchId(null);
    } else {
      setSelectedBatchId(parseInt(value));
    }
  };

  const toggleExpandRecord = (recordId: number) => {
    setExpandedRecords(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Unknown date';
    try {
      const ts = String(timestamp).trim();
      console.log('Formatting timestamp:', ts);

      // If we received a date-only string (YYYY-MM-DD), avoid timezone conversion
      const dateOnlyMatch = ts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnlyMatch) {
        const year = parseInt(dateOnlyMatch[1], 10);
        const monthIndex = parseInt(dateOnlyMatch[2], 10) - 1; // 0-based
        const day = parseInt(dateOnlyMatch[3], 10);
        const localDate = new Date(year, monthIndex, day);
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(localDate);
      }

      // Otherwise, treat as full timestamp and show local date and time
      const date = new Date(ts);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', ts);
        return 'Invalid date';
      }
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return 'Invalid date';
    }
  };

  // Display function for takenBy
  const formatRecordedBy = (record: FermentationMaintenanceRecord) => {
    if (record.takenBy && (record.takenBy.first_name || record.takenBy.last_name)) {
      return (
        <div className="flex flex-col">
          <span className="font-medium">{`${record.takenBy.first_name || ''} ${record.takenBy.last_name || ''}`.trim()}</span>
          <span className="text-xs text-monday-text-muted">{formatTimestamp(record.reading_timestamp).split(',')[0]}</span>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col">
          <span className="font-medium">User ID: {record.taken_by_id || 'Unknown'}</span>
          <span className="text-xs text-monday-text-muted">{formatTimestamp(record.reading_timestamp).split(',')[0]}</span>
        </div>
      );
    }
  };

  // Check if a measurement value exists and has a value
  const hasMeasurement = (value: any): boolean => {
    return value !== undefined && value !== null;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fermentation Maintenance"
        subtitle="Track and manage fermentation maintenance records."
        variant="compact"
        actions={(
          <PermissionButton
            resource="fermentationMaintenance"
            action="create"
            onClick={() => openModal()}
          >
            Add New Record
          </PermissionButton>
        )}
      />

      <ErrorMessageComponent error={error} className="mt-4" showValidationErrors={false} messageOnly={true} />

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-4">
        <label htmlFor="batch-filter" className="form-label">
          Filter by Wine Batch:
        </label>
        <select
          id="batch-filter"
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-monday-border focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm rounded-xl transition-all duration-150"
          value={selectedBatchId || ""}
          onChange={handleBatchChange}
        >
          <option value="">All Batches</option>
          {wineBatches.length > 0 ? (
            wineBatches.map((batch) => {
              // Handle different possible structures for batch data
              const batchId = batch.batch_id || batch.batchId || `#${batch.id}`;
              const batchName = batch.batch_name || batch.batchName || 'Unnamed Batch';
              const vineyardName = batch.vineyard?.name || batch.Vineyard?.name || '';

              console.log(`Rendering batch option: ID=${batch.id}, batchId=${batchId}, batchName=${batchName}, vineyard=${vineyardName}`);

              return (
                <option key={batch.id} value={batch.id}>
                  {vineyardName ? `${vineyardName} - ` : ''}{batchId} - {batchName}
                </option>
              );
            })
          ) : (
            <option value="" disabled>No wine batches available</option>
          )}
        </select>
        {wineBatches.length === 0 && !loading && (
          <p className="mt-1 text-sm text-monday-text-muted">No wine batches found. Please check your data.</p>
        )}
      </div>

      {/* Metrics selector and Time Series Chart */}
      <div className="mt-8 grid grid-cols-1 gap-4">
        <div className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-monday-text">Measurements Over Time</h3>
              <p className="text-sm text-monday-text-muted">Timeline reflects current filters</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsChartOpen(prev => !prev)}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text hover:bg-monday-hover"
                aria-expanded={isChartOpen}
                aria-controls="ferm-chart-section"
              >
                {isChartOpen ? 'Collapse' : 'Expand'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'brix', label: 'Brix' },
                { key: 'ph', label: 'pH' },
                { key: 'temperature', label: 'Temperature' },
                { key: 'alc', label: 'ALC' },
                { key: 'ta', label: 'TA' },
                { key: 'total_so2', label: 'Total SO2' },
                { key: 'rs', label: 'RS' },
                { key: 'sg', label: 'SG' },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() =>
                    setVisibleMetrics((prev) =>
                      prev.includes(m.key)
                        ? prev.filter((k) => k !== m.key)
                        : [...prev, m.key]
                    )
                  }
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${visibleMetrics.includes(m.key)
                    ? 'bg-primary-50 text-primary-600 border-primary-200'
                    : 'bg-white text-monday-text border-monday-border hover:bg-monday-hover'
                    }`}
                >
                  {m.label}
                </button>
              ))}
              <button
                onClick={() => setVisibleMetrics([])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Hide all
              </button>
              <button
                onClick={() => setVisibleMetrics(['brix', 'ph', 'temperature'])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Core
              </button>
            </div>
          </div>

          {isChartOpen && (() => {
            const records = maintenanceRecords.slice().sort((a, b) => {
              return new Date(a.reading_timestamp).getTime() - new Date(b.reading_timestamp).getTime();
            });
            const labels = records.map((r) => {
              try {
                return formatTimestamp(r.reading_timestamp);
              } catch {
                return 'Invalid date';
              }
            });

            const metricDefs: Record<string, { label: string; color: string; getter: (r: any) => number | null }> = {
              brix: { label: 'Brix', color: METRIC_COLOR_MAP.brix, getter: (r) => (r.brix ?? null) },
              ph: { label: 'pH', color: METRIC_COLOR_MAP.ph, getter: (r) => (r.ph ?? null) },
              temperature: { label: 'Temperature', color: METRIC_COLOR_MAP.temperature, getter: (r) => (r.temperature ?? null) },
              alc: { label: 'ALC', color: METRIC_COLOR_MAP.alcohol, getter: (r) => (r.alc ?? null) },
              ta: { label: 'TA', color: METRIC_COLOR_MAP.ta, getter: (r) => (r.ta ?? null) },
              total_so2: { label: 'Total SO2', color: METRIC_COLOR_MAP.totalSo2, getter: (r) => (r.total_so2 ?? null) },
              rs: { label: 'RS', color: METRIC_COLOR_MAP.rs, getter: (r) => (r.rs ?? null) },
              sg: { label: 'SG', color: METRIC_COLOR_MAP.sg, getter: (r) => (r.sg ?? null) },
            };

            const datasets = visibleMetrics
              .filter((k) => metricDefs[k])
              .map((k) => {
                const def = metricDefs[k];
                return {
                  label: def.label,
                  data: records.map((r) => def.getter(r)),
                  borderColor: def.color,
                  backgroundColor: def.color + '0D',
                } as any;
              });

            const nonEmptyDatasets = datasets.filter((ds: any) => Array.isArray(ds.data) && ds.data.some((v: any) => v !== null && Number.isFinite(Number(v))));

            return (
              <div className="mt-4" id="ferm-chart-section">
                {nonEmptyDatasets.length > 0 ? (
                  <ResponsiveChart
                    type="line"
                    title={selectedBatchId ? 'Batch Measurements' : 'All Measurements'}
                    data={{ labels, datasets: nonEmptyDatasets } as any}
                    height="360px"
                    variant="embedded"
                    instanceKey={`ferm-${selectedBatchId || 'all'}-${visibleMetrics.join(',')}-${records.length}`}
                  />
                ) : (
                  <div className="bg-white rounded-xl shadow border border-monday-border-light p-8 text-center">
                    <svg className="mx-auto h-12 w-12 text-monday-status-neutral mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-monday-text font-medium text-sm mb-1">No data to display</p>
                    <p className="text-monday-text-muted text-xs">Select metrics above or add records to see insights</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-monday-border">
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6">Wine Batch</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Date & Time</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Recorded By</th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-monday-border-light bg-white">
                    {maintenanceRecords.length > 0 ? (
                      maintenanceRecords.map((record) => (
                        <React.Fragment key={record.id}>
                          <tr>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {record.wineBatch?.vineyard?.name ? `${record.wineBatch.vineyard.name} - ${record.wineBatch?.batch_id || 'No ID'}` : record.wineBatch?.batch_id || `Record #${record.id}`}
                                </span>
                                <span className="text-monday-text-muted">{record.wineBatch?.batch_name || 'No batch name'}</span>
                                <ExpandChevron isExpanded={expandedRecords.includes(record.id)} onClick={(e) => { e.stopPropagation(); toggleExpandRecord(record.id); }} />
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{formatTimestamp(record.reading_timestamp)}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{formatRecordedBy(record)}</td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <ExpandChevron isExpanded={expandedRecords.includes(record.id)} onClick={(e) => { e.stopPropagation(); toggleExpandRecord(record.id); }} />
                              <PermissionButton
                                resource="fermentationMaintenance"
                                action="update"
                                onClick={() => openModal(record)}
                                variant="secondary"
                                className="!px-2 !py-1 text-sm !mr-2"
                              >
                                Edit
                              </PermissionButton>
                              {isAdmin && (
                                <button onClick={() => handleDelete(record.id)} className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 border border-red-200">Delete</button>
                              )}
                            </td>
                          </tr>
                          <ExpandableRow isExpanded={expandedRecords.includes(record.id)} colSpan={4}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {hasMeasurement(record.temperature) && (<div className="text-sm"><span className="font-medium text-monday-text">Temperature: </span><span className="text-monday-text">{record.temperature}°C</span></div>)}
                              {hasMeasurement(record.rs) && (<div className="text-sm"><span className="font-medium text-monday-text">RS: </span><span className="text-monday-text">{record.rs}</span></div>)}
                              {hasMeasurement(record.sg) && (<div className="text-sm"><span className="font-medium text-monday-text">SG: </span><span className="text-monday-text">{record.sg}</span></div>)}
                              {hasMeasurement(record.total_so2) && (<div className="text-sm"><span className="font-medium text-monday-text">Total SO2: </span><span className="text-monday-text">{record.total_so2}</span></div>)}
                              {hasMeasurement(record.et) && (<div className="text-sm"><span className="font-medium text-monday-text">ET: </span><span className="text-monday-text">{record.et}</span></div>)}
                              {hasMeasurement(record.ph) && (<div className="text-sm"><span className="font-medium text-monday-text">pH: </span><span className="text-monday-text">{record.ph}</span></div>)}
                              {hasMeasurement(record.ta) && (<div className="text-sm"><span className="font-medium text-monday-text">TA: </span><span className="text-monday-text">{record.ta}</span></div>)}
                              {hasMeasurement(record.alc) && (<div className="text-sm"><span className="font-medium text-monday-text">ALC: </span><span className="text-monday-text">{record.alc}%</span></div>)}
                              {hasMeasurement(record.density_sg) && (<div className="text-sm"><span className="font-medium text-monday-text">Density SG: </span><span className="text-monday-text">{record.density_sg}</span></div>)}
                              {hasMeasurement(record.brix) && (<div className="text-sm"><span className="font-medium text-monday-text">Brix: </span><span className="text-monday-text">{record.brix}</span></div>)}
                              {hasMeasurement(record.fk15_g_hl) && (<div className="text-sm"><span className="font-medium text-monday-text">FK15 g/HL: </span><span className="text-monday-text">{record.fk15_g_hl}</span></div>)}
                              {hasMeasurement(record.wine_volume) && (<div className="text-sm"><span className="font-medium text-monday-text">Wine Volume: </span><span className="text-monday-text">{record.wine_volume}</span></div>)}
                              {hasMeasurement(record.bisulfite) && (<div className="text-sm"><span className="font-medium text-monday-text">Bisulfite: </span><span className="text-monday-text">{record.bisulfite} mg/L</span></div>)}
                              {hasMeasurement(record.tartaric_acid) && (<div className="text-sm"><span className="font-medium text-monday-text">Tartaric Acid: </span><span className="text-monday-text">{record.tartaric_acid} g/L</span></div>)}
                              {hasMeasurement(record.yeast_strain) && (<div className="text-sm"><span className="font-medium text-monday-text">Yeast Strain: </span><span className="text-monday-text">{record.yeast_strain}</span></div>)}
                              {hasMeasurement(record.yeast_type) && (<div className="text-sm"><span className="font-medium text-monday-text">Yeast Type: </span><span className="text-monday-text">{record.yeast_type}</span></div>)}
                              {hasMeasurement(record.yeast_volume) && (<div className="text-sm"><span className="font-medium text-monday-text">Yeast Volume: </span><span className="text-monday-text">{record.yeast_volume}</span></div>)}
                              {record.notes && (<div className="text-sm col-span-3"><span className="font-medium text-monday-text">Notes: </span><span className="text-monday-text">{record.notes}</span></div>)}
                              <div className="text-sm col-span-3 mt-2 pt-2 border-t border-monday-border-light"><span className="font-medium text-monday-text">Recorded by: </span><span className="text-monday-text">{record.takenBy ? `${record.takenBy.first_name || ''} ${record.takenBy.last_name || ''}`.trim() || `User ID: ${record.taken_by_id}` : `User ID: ${record.taken_by_id}`}</span><span className="text-monday-text-muted ml-2">on {formatTimestamp(record.reading_timestamp)}</span></div>
                            </div>
                          </ExpandableRow>
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-4 text-center text-sm text-monday-text-muted">
                          No fermentation maintenance records found. Click "Add New Record" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="sm:hidden space-y-4">
          {maintenanceRecords.length > 0 ? (
            maintenanceRecords.map((record) => (
              <div key={record.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-base text-monday-text">
                      {record.wineBatch?.vineyard?.name ? `${record.wineBatch.vineyard.name} - ${record.wineBatch?.batch_id || 'No ID'}` : record.wineBatch?.batch_id || `Record #${record.id}`}
                    </div>
                    <div className="text-xs text-monday-text-muted">{record.wineBatch?.batch_name || 'No batch name'}</div>
                  </div>
                  <PermissionButton
                    resource="fermentationMaintenance"
                    action="update"
                    onClick={() => openModal(record)}
                    variant="secondary"
                    className="!py-1 !px-3 !text-xs"
                  >
                    Edit
                  </PermissionButton>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-monday-text">
                  <div><span className="font-medium">Date:</span> {formatTimestamp(record.reading_timestamp)}</div>
                  <div><span className="font-medium">By:</span> {record.takenBy ? `${record.takenBy.first_name || ''} ${record.takenBy.last_name || ''}`.trim() || `User ID: ${record.taken_by_id}` : `User ID: ${record.taken_by_id}`}</div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-monday-text-muted">
                  {/* Show a few key measurements inline, rest in expandable details */}
                  {hasMeasurement(record.temperature) && (<div>Temp: {record.temperature}°C</div>)}
                  {hasMeasurement(record.ph) && (<div>pH: {record.ph}</div>)}
                  {hasMeasurement(record.brix) && (<div>Brix: {record.brix}</div>)}
                  {hasMeasurement(record.alc) && (<div>ALC: {record.alc}%</div>)}
                  {hasMeasurement(record.total_so2) && (<div>SO2: {record.total_so2}</div>)}
                  {hasMeasurement(record.sg) && (<div>SG: {record.sg}</div>)}
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => toggleExpandRecord(record.id)} className="flex-1 py-2 rounded bg-monday-header text-monday-text font-medium text-xs active:bg-monday-hover">{expandedRecords.includes(record.id) ? 'Hide Details' : 'Show Details'}</button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(record.id)} className="flex-1 py-2 rounded bg-red-50 text-red-700 font-medium text-xs active:bg-red-100">Delete</button>
                  )}
                </div>
                {expandedRecords.includes(record.id) && (
                  <div className="mt-2 border-t pt-2 space-y-1 text-xs text-monday-text">
                    {hasMeasurement(record.rs) && (<div><span className="font-medium">RS:</span> {record.rs}</div>)}
                    {hasMeasurement(record.sg) && (<div><span className="font-medium">SG:</span> {record.sg}</div>)}
                    {hasMeasurement(record.et) && (<div><span className="font-medium">ET:</span> {record.et}</div>)}
                    {hasMeasurement(record.ta) && (<div><span className="font-medium">TA:</span> {record.ta}</div>)}
                    {hasMeasurement(record.density_sg) && (<div><span className="font-medium">Density SG:</span> {record.density_sg}</div>)}
                    {hasMeasurement(record.fk15_g_hl) && (<div><span className="font-medium">FK15 g/HL:</span> {record.fk15_g_hl}</div>)}
                    {hasMeasurement(record.wine_volume) && (<div><span className="font-medium">Wine Volume:</span> {record.wine_volume}</div>)}
                    {hasMeasurement(record.bisulfite) && (<div><span className="font-medium">Bisulfite:</span> {record.bisulfite} mg/L</div>)}
                    {hasMeasurement(record.tartaric_acid) && (<div><span className="font-medium">Tartaric Acid:</span> {record.tartaric_acid} g/L</div>)}
                    {hasMeasurement(record.yeast_strain) && (<div><span className="font-medium">Yeast Strain:</span> {record.yeast_strain}</div>)}
                    {hasMeasurement(record.yeast_type) && (<div><span className="font-medium">Yeast Type:</span> {record.yeast_type}</div>)}
                    {hasMeasurement(record.yeast_volume) && (<div><span className="font-medium">Yeast Volume:</span> {record.yeast_volume}</div>)}
                    {record.notes && (<div><span className="font-medium">Notes:</span> {record.notes}</div>)}
                    <div className="text-monday-text-muted mt-2">Recorded on {formatTimestamp(record.reading_timestamp)}</div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center text-monday-text-muted py-8">No fermentation maintenance records found. Click "Add New Record" to create one.</div>
          )}
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingRecord ? 'Edit Maintenance Record' : 'Add New Maintenance Record'} size="full">
        <div className="mobile-form-container">
          <Formik
            key={editingRecord ? `edit-${editingRecord.id}` : 'new'}
            initialValues={(() => {
              // Use fermentation_id from record when editing; when creating with batch filter, preselect first fermentation of that batch
              let initialFermentationId: number | null = editingRecord?.fermentation_id ?? null;
              if (!initialFermentationId && selectedBatchId && fermentations.length > 0) {
                const firstForBatch = fermentations.find((f: any) => (f.batch_id ?? f.batchId ?? f.wineBatch?.id) === selectedBatchId);
                initialFermentationId = firstForBatch ? (firstForBatch.id ?? null) : null;
              }

              return {
                fermentation_id: initialFermentationId,
                temperature: editingRecord?.temperature || null,
                rs: editingRecord?.rs || null,
                sg: editingRecord?.sg || null,
                total_so2: editingRecord?.total_so2 || null,
                et: editingRecord?.et || null,
                ph: editingRecord?.ph || null,
                ta: editingRecord?.ta || null,
                alc: editingRecord?.alc || null,
                density_sg: editingRecord?.density_sg || null,
                brix: editingRecord?.brix || null,
                fk15_g_hl: editingRecord?.fk15_g_hl || null,
                wine_volume: editingRecord?.wine_volume || null,
                bisulfite: editingRecord?.bisulfite || null,
                tartaric_acid: editingRecord?.tartaric_acid || null,
                yeast_strain: editingRecord?.yeast_strain || '',
                yeast_type: editingRecord?.yeast_type || '',
                yeast_volume: editingRecord?.yeast_volume || '',
                reading_timestamp: editingRecord?.reading_timestamp
                  ? toDateInputValue(editingRecord.reading_timestamp)
                  : todayDateInputValue(),
                notes: editingRecord?.notes || ''
              };
            })()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isSubmitting, setFieldValue, values }) => (
                <Form className="space-y-4">
                  <div>
                    <label htmlFor="fermentation_id" className="form-label mb-1">
                      Fermentation <span className="form-required-dot"></span>
                    </label>
                    <select
                      id="fermentation_id"
                      name="fermentation_id"
                      value={values.fermentation_id ? String(values.fermentation_id) : ''}
                      className="mobile-form-input"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const val = e.target.value ? parseInt(e.target.value, 10) : null;
                        setFieldValue('fermentation_id', val);
                      }}
                    >
                      <option value="">Select a fermentation</option>
                      {(selectedBatchId
                        ? fermentations.filter((f: any) => Number(f.batch_id ?? f.batchId ?? f.wineBatch?.id) === selectedBatchId)
                        : fermentations
                      ).map((ferm: any) => {
                        const batchId = ferm.wineBatch?.batch_id ?? ferm.wineBatch?.batchId ?? ferm.batch_id ?? `#${ferm.batch_id ?? ''}`;
                        const batchName = ferm.wineBatch?.batch_name ?? ferm.wineBatch?.batchName ?? 'Unnamed Batch';
                        const vineyardName = ferm.wineBatch?.vineyard?.name ?? ferm.wineBatch?.Vineyard?.name ?? '';
                        const vesselName = ferm.vessel?.name ?? ferm.Vessel?.name ?? '';
                        const status = ferm.status ?? '';
                        const label = [vineyardName, batchId, batchName, vesselName, status].filter(Boolean).join(' - ');

                        return (
                          <option key={ferm.id} value={ferm.id}>
                            {label || `Fermentation #${ferm.id}`}
                          </option>
                        );
                      })}
                    </select>
                    <ErrorMessage name="fermentation_id" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div>
                    <label htmlFor="reading_timestamp" className="form-label mb-1">
                      Date <span className="form-required-dot"></span>
                    </label>
                    <Field
                      type="date"
                      id="reading_timestamp"
                      name="reading_timestamp"
                      className="mobile-form-input"
                    />
                    <p className="mt-1 text-xs text-monday-text-muted">
                      When this measurement was taken (defaults to today)
                    </p>
                    <ErrorMessage name="reading_timestamp" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="temperature" className="form-label mb-1">
                        Temperature (°C)
                      </label>
                      <Field type="number" step="0.1" id="temperature" name="temperature" className="mobile-form-input" placeholder="e.g., 18.5" />
                      <ErrorMessage name="temperature" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="rs" className="form-label mb-1">
                        RS
                      </label>
                      <Field type="number" step="0.1" id="rs" name="rs" className="mobile-form-input" placeholder="e.g., 1.2" />
                      <ErrorMessage name="rs" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="total_so2" className="form-label mb-1">
                        Total SO2
                      </label>
                      <Field type="number" step="0.1" id="total_so2" name="total_so2" className="mobile-form-input" placeholder="e.g., 45.0" />
                      <ErrorMessage name="total_so2" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="sg" className="form-label mb-1">
                        SG
                      </label>
                      <Field type="number" step="0.001" id="sg" name="sg" className="mobile-form-input" placeholder="e.g., 0.998" />
                      <ErrorMessage name="sg" component="div" className="mt-1 text-sm text-red-600" />
                    </div>


                    <div>
                      <label htmlFor="et" className="form-label mb-1">
                        ET
                      </label>
                      <Field type="number" step="0.1" id="et" name="et" className="mobile-form-input" placeholder="e.g., 0.2" />
                      <ErrorMessage name="et" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="ph" className="form-label mb-1">
                        pH
                      </label>
                      <Field type="number" step="0.01" id="ph" name="ph" className="mobile-form-input" placeholder="e.g., 3.45" />
                      <ErrorMessage name="ph" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="brix" className="form-label mb-1">
                        Brix
                      </label>
                      <Field type="number" step="0.1" id="brix" name="brix" className="mobile-form-input" placeholder="e.g., 20.5" />
                      <ErrorMessage name="brix" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="ta" className="form-label mb-1">
                        TA
                      </label>
                      <Field type="number" step="0.1" id="ta" name="ta" className="mobile-form-input" placeholder="e.g., 6.2" />
                      <ErrorMessage name="ta" component="div" className="mt-1 text-sm text-red-600" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="yeast_strain" className="form-label mb-1">
                        Yeast Strain
                      </label>
                      <Field type="text" id="yeast_strain" name="yeast_strain" className="mobile-form-input" placeholder="e.g., EC1118, RC212" />
                      <ErrorMessage name="yeast_strain" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="yeast_type" className="form-label mb-1">
                        Yeast Type
                      </label>
                      <Field type="text" id="yeast_type" name="yeast_type" className="mobile-form-input" placeholder="e.g., Saccharomyces cerevisiae" />
                      <ErrorMessage name="yeast_type" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    <div>
                      <label htmlFor="yeast_volume" className="form-label mb-1">
                        Yeast Volume
                      </label>
                      <Field type="text" id="yeast_volume" name="yeast_volume" className="mobile-form-input" placeholder="e.g., 25g, 1 packet" />
                      <ErrorMessage name="yeast_volume" component="div" className="mt-1 text-sm text-red-600" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="notes" className="form-label mb-1">
                      Notes
                    </label>
                    <Field as="textarea" id="notes" name="notes" rows={3} className="mobile-form-input resize-none" placeholder="Optional notes about this maintenance record" />
                    <ErrorMessage name="notes" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <button type="submit" disabled={isSubmitting} className="mobile-btn mobile-btn-primary w-full sm:col-start-2 sm:text-sm">
                      {isSubmitting ? 'Saving...' : editingRecord ? 'Update' : 'Save'}
                    </button>
                    <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary mt-3 w-full sm:mt-0 sm:col-start-1 sm:text-sm">
                      Cancel
                    </button>
                  </div>
                </Form>
            )}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default FermentationMaintenance;
